package com.ilussobsa.views

import com.ilussobsa.*
import com.ilussobsa.Strings
import com.ilussobsa.sdk.*
import com.ilussobsa.utils.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.*
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.*
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.children
import com.lightningkite.kiteui.views.l2.field
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.kiteui.views.l2.lazyExpanding
import com.lightningkite.lightningdb.*
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import com.lightningkite.serialization.lensPath
import com.lightningkite.serialization.notNull
import kotlinx.coroutines.launch

@Routable("/users")
class UsersScreen() : Page, AdminOnly {

    @QueryParameter("sort")
    val sort = Signal<List<SortPart<User>>>(sort { it.name.ascending() })

    @QueryParameter("query")
    val search = Signal("")

    @QueryParameter("selectedUser")
    val selectedUser = Signal<UUID?>(null)

    private val showOnlyInactive = Signal(false)

    override val title: Reactive<String> = remember {
        listOfNotNull(
            Strings.users,
        ).joinToString(" ")
    }
    val query = remember {
        Query(
            condition<User> {
                Condition.And(
                    listOfNotNull(
                        search.debounce(500L)().takeUnless { it.isBlank() }
                            ?.let { s ->
                                Condition.And(s.split(' ').filter { it.isNotBlank() }.map { s ->
                                    Condition.Or(
                                        listOf(
                                            it.name.contains(s, true),
                                            it.email.contains(s, true),
                                            it.phoneNumber.notNull.contains(s, true),
                                            it.biddingId.notNull.contains(s, true)
                                        )
                                    )
                                })
                            },
                    )
                )
            },
            orderBy = sort(),
            limit = 500,
        )
    }

    override fun ViewWriter.render() = col {
        tabTheme - scrollsHorizontally - row {
            select {
                bind(
                    sort, Constant(
                        listOf(
                            sort<User> { it.name.ascending() },
                            sort<User> { it.lastPresent.descending() },
                        )
                    )
                ) {
                    when (it) {
                        sort<User> { it.name.ascending() } -> "Name A-Z"
                        sort<User> { it.lastPresent.descending() } -> "Last Present"
                        else -> "Unknown Sort"
                    }
                }
            }
        }


        val toListen = remember {
            currentSession.awaitNotNull().user.query(query())
        }
        expanding - listDetailV2(
            selected = selectedUser,
            list = { view ->
                col {
                    card - row {
                        centered - sizeConstraints(
                            width = 1.5.rem,
                            height = 1.5.rem
                        ) - icon(
                            Icon.search,
                            Strings.search
                        )

                        centered - expanding - textField {
                            hint = Strings.search
                            content bind search
                        }
                        centered - compact - button {
                            ::shown { search().isNotEmpty() }
                            sizeConstraints(width = 1.5.rem, height = 1.5.rem) - icon(
                                Icon.close,
                                Strings.clearSearch
                            )
                            onClick {
                                search set ""
                            }
                        }
                    }
                    expanding - navList - frame {
                        val items = remember { toListen()() }
                        recyclerView {
                            children(
                                items = items,
                                id = { it._id }
                            ) {
                                psuedoRadioToggleButton(remember { it()._id }, selectedUser, view) {
                                    row {
                                        centered - profilePicture(it)
                                        compact - col {
                                            text {
                                                ::content { it().name.takeUnless { it.isBlank() } ?: "<no name provided>" }
                                            }
                                            subtext { ::content { it().email } }
                                            subtext { ::content { it().phoneNumber ?: "No phone number" } }
                                        }
                                    }
                                }
                            }
                        }
                        centered - text {
                            content = Strings.noUsersMatchedTheseConditions
                            exists = false
                            ::shown { items().isEmpty() }
                        }
                    }
                }
            },
            detail = {
                it?.let { UserScreen(it) } ?: object : Page {
                    override fun ViewWriter.render() = nothingSelected(Strings.pleaseSelectAUserOnTheLeft)
                }
            }
        )
    }
}

@Routable("/users/{selectedUser}")
class UserScreen(val selectedUser: UUID) : Page {
    val user = LateInitSignal<User>()
    override fun ViewWriter.render() = scrolling - frame {
        val selected = remember { currentSession.awaitNotNull().user.get(selectedUser)() }
        card - scrolling - col {
            launch {
                user.value = selected() ?: User(email = "")
            }
            centered - row {
                centered - button {
                    profilePicture(user)
                    onClick {
                        ExternalServices.requestFile(listOf("image/png", "image/jpeg")) { image ->
                            if (image == null) return@requestFile
                            launch {
                                val session = currentUserSessionNullable.await() ?: return@launch
                                try {
                                    val futureCallToken =
                                        session.api.uploadFileForRequest().upload(RequestBodyFile(image))
                                    tempImagePointers[futureCallToken] = ImageLocal(image)
                                    user.modify { it.copy(profilePicture = futureCallToken) }
                                } catch (e: Exception) {
                                    Exception("Error updating profile picture", e).report()
                                    alert(Strings.error, Strings.failedToUploadPicture)
                                }
                            }
                        }
                    }
                }

            }
            field(Strings.name1) {
                textInput {
                    keyboardHints = KeyboardHints.title
                    hint = "Name"
                    content bind user.lensPath { it.name }
                }
            }
            field(Strings.email1) {
                textInput {
                    keyboardHints = KeyboardHints.email
                    hint = "Email"
                    content bind user.lensPath { it.email }
                }
            }
            field(Strings.phoneNumber) {
                textInput {
                    keyboardHints = KeyboardHints.phone
                    hint = "Phone Number"
                    content bind user.lensPath { it.phoneNumber }.nullToBlank()
                }
            }
            field(Strings.accountStatus) {
                val allowedRoles = listOf(UserRole.Disabled, UserRole.UnverifiedCustomer, UserRole.Customer, UserRole.Manager)
                select {
                    ::shown { user().role in allowedRoles }
                    bind(
                        edits = user.lensPath { it.role },
                        data = Constant(allowedRoles),
                        render = { Strings.userRoleNames(it) }
                    )
                }
                text {
                    ::shown { user().role !in allowedRoles }
                    ::content { Strings.userRoleNames(user().role) }
                }
            }
            field(Strings.bidderId) {
                text {
                    exists = false
                }
                text {
                    exists = true
                    ::content { user().biddingId ?: Strings.noBidderId }
                }
            }
            field(Strings.financingSource) {
                select {
                    bind(
                        edits = user.lensPath { it.financingSource },
                        data = Constant(listOf(null, *FinancingSource.entries.toTypedArray())),
                        render = { Strings.financingSourceName(it) }
                    )
                }
            }
            lazyExpanding(user.lensPath { it.financingSource }.equalTo(FinancingSource.OwnFinancing)) {
                val prop = Signal(ExternalFinancingForm())
                reactiveSuspending {
                    selected()?.currentExternalFinancingForm?.let { currentFinancingFormId ->
                        val existingForm = currentSession().externalFinancingForm[currentFinancingFormId].awaitOnce()
                            ?: return@reactiveSuspending
                        prop set existingForm
                    }
                }
                val save = Action(Strings.submit, Icon.done) {
                    val form = prop().copy(user = currentUser()?._id ?: nullUuid)
                    currentUserSession().externalFinancingForm.upsert(form)
                }
                card - col {
                    h3("External Financing Form")
                    externalFinancingForm(prop)
                    important - gravity(Align.Start, Align.Center) - button {
                        text(Strings.save)
                        action = save
                    }
                }
            }
            row {
                expanding - space()
                centered - button {
//                        ::shown {
//                            currentUser() != selected() && selected()?.let { currentUser()?.mayMasqueradeAs(it) } == true
//                        }
                    text(Strings.masquerade)
                    onClick {
                        masqueradeUserId set selected()?._id?.toString()
                        pageNavigator.reset(DashboardScreen())
                    }
                }
                centered - important - button {
                    centered - text {
                        val changes = remember {
                            user() != selected()
                        }
                        ::content {
                            if (changes()) Strings.save else Strings.saved
                        }
                        ::enabled { changes() }
                    }
                    onClick {
                        currentSession.awaitNotNull().user.get(selectedUser).set(user())
                    }
                }
                expanding - space()
            }
        }
    }
}
